import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { STATE_CODES } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, ShoppingCart, Loader2, Trash2, Search, MoreVertical, Pencil,
  CheckCircle2, CreditCard, Banknote, Smartphone, Building2, Wallet,
  Download, CheckSquare, X, Clock, CircleDollarSign,
  Upload, ShieldAlert, RotateCcw, Ban, HardDrive, ScanLine
} from "lucide-react";
import { toast } from "sonner";

const PAID_VIA_OPTIONS = [
  { value: "UPI", icon: Smartphone, label: "UPI" },
  { value: "Cash", icon: Banknote, label: "Cash" },
  { value: "Bank Transfer", icon: Building2, label: "Bank Transfer" },
  { value: "Credit Card", icon: CreditCard, label: "Credit Card" },
  { value: "Corporate Card", icon: Wallet, label: "Corporate Card" },
];

const CATEGORIES = [
  "Raw Materials", "Inventory / Stock", "Components / Parts",
  "Packaging Materials", "Consumables", "Sub-contracting",
  "Project Materials", "Freight Inward", "Import Duty / Customs",
  "Other Direct Costs"
];

const GST_SLAB_OPTIONS = [0, 5, 12, 18, 28];

/** Resolve user's state name from numeric `state_code` (e.g. "27" → "Maharashtra"). */
function getUserStateName(user) {
  const code = (user?.state_code || "").toString().trim();
  if (!code) return "";
  const match = STATE_CODES.find((s) => s.code === code);
  return match ? match.name : "";
}

/**
 * Extract the 2-character state code from a GSTIN string. Returns "" when
 * the input is not a valid 15-char GSTIN OR the leading 2 chars are not
 * numeric. GSTIN format: `<2 digit state code><10 char PAN><1 entity><1 Z><1 checksum>`.
 */
function gstinStateCode(gstin) {
  const s = (gstin || "").toString().trim().toUpperCase();
  if (s.length < 2) return "";
  const head = s.slice(0, 2);
  if (!/^[0-3][0-9]$/.test(head)) return "";
  return head;
}

/** Lower-case + trim + collapse whitespace for safe state-name comparison. */
function normaliseState(s) {
  return (s || "").toString().toLowerCase().trim().replace(/\s+/g, " ");
}

const emptyPurchaseLineItem = () => ({
  name: "", hsn_code: "", quantity: 1, unit_price: 0,
  gst_rate_pct: 18, cgst: 0, sgst: 0, igst: 0, taxable_value: 0,
});

/** Recompute taxable_value + tax split for a single line based on interstate flag. */
function recomputeLine(li, interstate) {
  const qty = Number(li.quantity) || 0;
  const price = Number(li.unit_price) || 0;
  const rate = Number(li.gst_rate_pct) || 0;
  const taxable = +(qty * price).toFixed(2);
  const taxTotal = +(taxable * rate / 100).toFixed(2);
  const cgst = interstate ? 0 : +(taxTotal / 2).toFixed(2);
  const sgst = interstate ? 0 : +(taxTotal / 2).toFixed(2);
  const igst = interstate ? taxTotal : 0;
  return { ...li, taxable_value: taxable, cgst, sgst, igst };
}

/** Purchase-specific line-items editor with HSN, GST%, and per-line tax preview. */
function PurchaseLineItemsEditor({ items, onChange, interstate }) {
  const updateItem = (idx, patch) => {
    const next = items.map((li, i) => {
      if (i !== idx) return li;
      const merged = { ...li, ...patch };
      return recomputeLine(merged, interstate);
    });
    onChange(next);
  };
  const addRow = () => onChange([...items, recomputeLine(emptyPurchaseLineItem(), interstate)]);
  const removeRow = (idx) => {
    if (items.length <= 1) return;
    onChange(items.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-3" data-testid="purchase-line-items-section">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-[#0f172a]">Line Items</Label>
        <Button type="button" size="sm" variant="ghost" onClick={addRow}
          data-testid="purchase-add-line-item-btn"
          className="text-xs text-[#4338ca] hover:text-[#3730a3] h-7 px-2">
          <Plus className="h-3 w-3 mr-1" /> Add Row
        </Button>
      </div>

      {/* Header */}
      <div className="grid grid-cols-[1.6fr_90px_60px_90px_70px_110px_32px] gap-2 text-[10px] font-medium text-[#94a3b8] uppercase tracking-wider px-1">
        <span>Item Name</span>
        <span>HSN / SAC</span>
        <span className="text-center">Qty</span>
        <span className="text-right">Unit Price</span>
        <span className="text-center">GST %</span>
        <span className="text-right">Line Total</span>
        <span />
      </div>

      {items.map((li, idx) => {
        const lineTotal = +(li.taxable_value || 0) + +(li.cgst || 0) + +(li.sgst || 0) + +(li.igst || 0);
        return (
          <div key={idx} className="grid grid-cols-[1.6fr_90px_60px_90px_70px_110px_32px] gap-2 items-center"
            data-testid={`purchase-line-item-row-${idx}`}>
            <Input value={li.name} onChange={(e) => updateItem(idx, { name: e.target.value })}
              placeholder="Item description" className="h-9 text-sm"
              data-testid={`purchase-line-item-name-${idx}`} />
            <Input value={li.hsn_code} onChange={(e) => updateItem(idx, { hsn_code: e.target.value })}
              placeholder="HSN" className="h-9 text-xs font-mono"
              data-testid={`purchase-line-item-hsn-${idx}`} />
            <Input type="number" min="0" step="1" value={li.quantity}
              onChange={(e) => updateItem(idx, { quantity: e.target.value })}
              className="h-9 text-sm text-center"
              data-testid={`purchase-line-item-qty-${idx}`} />
            <Input type="number" min="0" step="0.01" value={li.unit_price}
              onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
              className="h-9 text-sm text-right font-mono"
              data-testid={`purchase-line-item-price-${idx}`} />
            <Select value={String(li.gst_rate_pct || 0)}
              onValueChange={(v) => updateItem(idx, { gst_rate_pct: Number(v) })}>
              <SelectTrigger className="h-9 text-xs" data-testid={`purchase-line-item-gst-${idx}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GST_SLAB_OPTIONS.map((r) => (
                  <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="h-9 px-2 flex items-center justify-end text-sm font-mono text-[#0f172a] bg-[#f8fafc] rounded-md border border-[#e2e8f0]"
              data-testid={`purchase-line-item-total-${idx}`}>
              {formatINR(lineTotal)}
            </div>
            <button type="button" onClick={() => removeRow(idx)} disabled={items.length <= 1}
              className="h-9 w-8 flex items-center justify-center rounded-md text-[#94a3b8] hover:text-red-500 hover:bg-red-50 disabled:opacity-30 transition-colors"
              data-testid={`purchase-remove-line-item-${idx}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default function PurchasesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isUnregistered = user?.gst_status === "unregistered";
  const userStateName = useMemo(() => getUserStateName(user), [user]);
  const [purchases, setPurchases] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [interstateManualOverride, setInterstateManualOverride] = useState(false);
  const [autoInterstateNote, setAutoInterstateNote] = useState("");
  const [cameFromCcScan, setCameFromCcScan] = useState(false);
  const fileRef = useRef(null);
  const scanRef = useRef(null);

  const [form, setForm] = useState({
    vendor_name: "", gst_number: "", category: "",
    description: "", date: new Date().toISOString().split("T")[0],
    due_date: "", paid_via: "",
    payment_status: "unpaid",
    bill_number: "", bill_date: "",
    line_items: [emptyPurchaseLineItem()],
    interstate: false,
    is_eligible_itc: true, reverse_charge: false,
    receipt_image: "",
  });

  useEffect(() => {
    api.get("/purchases").then((r) => setPurchases(r.data)).finally(() => setLoading(false));
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
  }, []);

  /**
   * Authoritative interstate detection from GSTINs.
   *
   * Compares the first 2 digits of the active business's GSTIN against the
   * first 2 digits of the typed Supplier GSTIN. When both codes are present
   * AND parseable, the GSTIN-based decision OVERRIDES the legacy vendor-
   * name-state lookup below (GSTIN state codes are the actual GST law
   * basis for POS / interstate classification). The user can still
   * override via the checkbox — manual override locks the rest of the
   * dialog session.
   */
  useEffect(() => {
    if (!dialogOpen) return;
    if (interstateManualOverride) return;
    const myCode = gstinStateCode(user?.gstin);
    const supplierCode = gstinStateCode(form.gst_number);
    if (!myCode || !supplierCode) return; // fall through to vendor-name matcher
    const shouldBeInterstate = myCode !== supplierCode;
    if (shouldBeInterstate !== form.interstate) {
      setForm((f) => ({
        ...f,
        interstate: shouldBeInterstate,
        line_items: (f.line_items || []).map((li) => recomputeLine(li, shouldBeInterstate)),
      }));
    }
    setAutoInterstateNote(
      shouldBeInterstate
        ? `Auto-detected interstate via GSTIN (supplier state ${supplierCode} ≠ your ${myCode})`
        : `Auto-detected intrastate via GSTIN (both in state ${myCode})`
    );
  }, [form.gst_number, user?.gstin, interstateManualOverride, dialogOpen, form.interstate]);

  /**
   * Auto-toggle "Interstate (IGST)" when the typed vendor matches a known vendor
   * whose state differs from the user's business state. Skipped once the user
   * has manually touched the checkbox during this dialog session OR when the
   * GSTIN-based matcher above has already made a decision.
   */
  useEffect(() => {
    if (!dialogOpen) return;
    if (interstateManualOverride) return;
    // GSTIN matcher takes priority — skip vendor-name fallback when both GSTINs parse.
    if (gstinStateCode(user?.gstin) && gstinStateCode(form.gst_number)) return;
    const typedName = normaliseState(form.vendor_name);
    if (!typedName) {
      if (autoInterstateNote) setAutoInterstateNote("");
      return;
    }
    if (!userStateName) return;
    const match = vendors.find((v) => normaliseState(v.name) === typedName);
    if (!match || !match.state) {
      if (autoInterstateNote) setAutoInterstateNote("");
      return;
    }
    const vendorState = normaliseState(match.state);
    const myState = normaliseState(userStateName);
    const shouldBeInterstate = vendorState !== myState;
    if (shouldBeInterstate !== form.interstate) {
      setForm((f) => ({
        ...f,
        interstate: shouldBeInterstate,
        line_items: (f.line_items || []).map((li) => recomputeLine(li, shouldBeInterstate)),
      }));
    }
    setAutoInterstateNote(
      shouldBeInterstate
        ? `Auto-detected interstate (vendor in ${match.state})`
        : `Auto-detected intrastate (vendor in ${match.state})`
    );
  }, [form.vendor_name, form.gst_number, user?.gstin, vendors, userStateName, interstateManualOverride, dialogOpen, form.interstate, autoInterstateNote]);

  // Detect prefill from Command Center scan-result navigation and open the modal pre-filled
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    const interstate = false;
    // Track rows whose GST rate Gemini couldn't read (gst_rate_pct === 0 from
    // the OCR pipeline = missing/unreadable). We surface a banner so the
    // accountant fixes them before saving — silently defaulting to 18% would
    // land wrong tax in the books.
    let unreadableRateRows = 0;
    const scannedItems = (prefill.line_items && prefill.line_items.length > 0)
      ? prefill.line_items.map((li) => {
          const rawRate = li.gst_rate_pct;
          const parsedRate = Number(rawRate);
          const rateMissing = rawRate === undefined || rawRate === null
            || rawRate === "" || parsedRate === 0;
          if (rateMissing) unreadableRateRows += 1;
          return recomputeLine({
            name: li.name || "",
            hsn_code: li.hsn_code || li.hsn_sac || "",
            quantity: Number(li.quantity) || 1,
            unit_price: Number(li.unit_price) || 0,
            // Keep 0 visible in the form so the row shows the inline warning;
            // user must explicitly pick a slab before saving.
            gst_rate_pct: rateMissing ? 0 : parsedRate,
          }, interstate);
        })
      : [recomputeLine({
          ...emptyPurchaseLineItem(),
          name: prefill.description || "Scanned item",
          unit_price: Number(prefill.amount) || 0,
        }, interstate)];
    setEditId(null);
    setForm({
      vendor_name: prefill.vendor_name || "",
      gst_number: prefill.gst_number || "",
      category: prefill.category || "Raw Materials",
      description: prefill.description || "",
      date: prefill.date || new Date().toISOString().split("T")[0],
      due_date: "",
      paid_via: prefill.paid_via || "",
      payment_status: prefill.paid_via ? "paid" : "unpaid",
      bill_number: prefill.bill_number || prefill.invoice_number || "",
      bill_date: prefill.bill_date || prefill.date || "",
      line_items: scannedItems,
      interstate,
      is_eligible_itc: true,
      reverse_charge: false,
      // Carry the original scanned image base64 through to the save payload
      // so the backend can upload it to the structured Drive vault folder
      // (BizFlowa / FY_YYYY-YY / Purchases / <Month>). Without this hand-off
      // the scan → save loop bypassed Drive entirely.
      receipt_image: prefill.original_image || "",
    });
    setDialogOpen(true);
    toast.success("Review the scanned details and save");

    // Surface non-blocking warnings the user should resolve before saving.
    if (unreadableRateRows > 0) {
      const inv = scannedItems.reduce(
        (s, li) => s + (Number(li.quantity || 0) * Number(li.unit_price || 0)) + Number(li.cgst || 0) + Number(li.sgst || 0) + Number(li.igst || 0),
        0
      );
      toast.warning(
        `${unreadableRateRows} line item${unreadableRateRows > 1 ? "s have" : " has"} no GST rate. ` +
        `Pick a slab — the form total (${formatINR(inv)}) won't match the invoice until you do.`,
        { duration: 8000 }
      );
    }
    // GSTIN-based interstate matcher only fires when BOTH GSTINs parse.
    // Warn when the supplier is unregistered so the user knows to flip
    // interstate manually if the supplier ships from a different state.
    if (!gstinStateCode(prefill.gst_number)) {
      toast.warning(
        "Supplier GSTIN not detected — please toggle 'Interstate (IGST)' " +
        "manually if this bill is from a different state.",
        { duration: 7000 }
      );
    }

    setCameFromCcScan(true);
    // Clear the router state so a refresh doesn't re-open the modal
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const handleChange = (field, value) => setForm((f) => {
    const next = { ...f, [field]: value };
    // When interstate toggles, recompute every line so tax split stays accurate
    if (field === "interstate") {
      next.line_items = (f.line_items || []).map((li) => recomputeLine(li, value));
      // Mark that the user explicitly touched the toggle so auto-detection backs off
      setInterstateManualOverride(true);
      setAutoInterstateNote("");
    }
    return next;
  });

  const resetForm = () => {
    setForm({
      vendor_name: "", gst_number: "", category: "",
      description: "", date: new Date().toISOString().split("T")[0],
      due_date: "", paid_via: "",
      payment_status: "unpaid",
      bill_number: "", bill_date: "",
      line_items: [emptyPurchaseLineItem()],
      interstate: false,
      is_eligible_itc: true, reverse_charge: false,
      receipt_image: "",
    });
    setEditId(null);
    setInterstateManualOverride(false);
    setAutoInterstateNote("");
  };

  const openCreate = () => { resetForm(); setDialogOpen(true); };
  const openEdit = (pur) => {
    const existingItems = (pur.line_items && pur.line_items.length > 0)
      ? pur.line_items.map((li) => ({
          name: li.name || "",
          hsn_code: li.hsn_code || li.hsn_sac || "",
          quantity: Number(li.quantity) || 1,
          unit_price: Number(li.unit_price) || 0,
          gst_rate_pct: Number(li.gst_rate_pct || pur.gst_rate || 18),
          cgst: Number(li.cgst) || 0,
          sgst: Number(li.sgst) || 0,
          igst: Number(li.igst) || 0,
          taxable_value: Number(li.taxable_value) || 0,
        }))
      : [emptyPurchaseLineItem()];
    const interstate = (pur.igst || 0) > 0;
    setForm({
      vendor_name: pur.vendor_name || "",
      gst_number: pur.gst_number || "", category: pur.category || "",
      description: pur.description || "",
      date: pur.date || new Date().toISOString().split("T")[0],
      due_date: pur.due_date || "", paid_via: pur.paid_via || "",
      payment_status: pur.payment_status || "unpaid",
      bill_number: pur.bill_number || "",
      bill_date: pur.bill_date || "",
      line_items: existingItems.map((li) => recomputeLine(li, interstate)),
      interstate,
      is_eligible_itc: pur.is_eligible_itc !== false,
      reverse_charge: pur.reverse_charge || false,
      receipt_image: "",
    });
    setEditId(pur.purchase_id);
    setDialogOpen(true);
    // Editing an existing record — preserve its stored interstate flag, don't auto-flip
    setInterstateManualOverride(true);
    setAutoInterstateNote("");
  };

  // Compute totals from line items
  const totals = useMemo(() => {
    const items = form.line_items || [];
    const taxable = items.reduce((s, li) => s + (Number(li.taxable_value) || 0), 0);
    const cgst = items.reduce((s, li) => s + (Number(li.cgst) || 0), 0);
    const sgst = items.reduce((s, li) => s + (Number(li.sgst) || 0), 0);
    const igst = items.reduce((s, li) => s + (Number(li.igst) || 0), 0);
    return {
      taxable_value: +taxable.toFixed(2),
      cgst: +cgst.toFixed(2),
      sgst: +sgst.toFixed(2),
      igst: +igst.toFixed(2),
      grand_total: +(taxable + cgst + sgst + igst).toFixed(2),
    };
  }, [form.line_items]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("File must be under 20MB"); return; }
    const reader = new FileReader();
    reader.onload = () => handleChange("receipt_image", reader.result);
    reader.readAsDataURL(file);
  };

  /** Scan an invoice (image or PDF) and pre-fill the Purchase modal. */
  const handleScanInvoice = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File must be under 20MB");
      e.target.value = "";
      return;
    }
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    setScanning(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const dataUrl = reader.result;
        const response = await api.post("/receipt/scan", { image: dataUrl });
        const d = response.data;
        setEditId(null);

        const interstate = false; // user toggles after if needed
        const defaultGst = 18;
        const scannedItems = (d.line_items && d.line_items.length > 0)
          ? d.line_items.map((li) => recomputeLine({
              name: li.name || "",
              hsn_code: li.hsn_code || li.hsn_sac || "",
              quantity: Number(li.quantity) || 1,
              unit_price: Number(li.unit_price) || 0,
              gst_rate_pct: Number(li.gst_rate_pct || defaultGst),
            }, interstate))
          : [recomputeLine({
              ...emptyPurchaseLineItem(),
              name: d.description || "Scanned item",
              unit_price: Number(d.amount) || 0,
            }, interstate)];

        setForm({
          vendor_name: d.vendor_name || "",
          gst_number: d.gst_number || "",
          category: d.category || "Raw Materials",
          description: d.description || "",
          date: d.date || new Date().toISOString().split("T")[0],
          due_date: "",
          paid_via: d.paid_via || "",
          payment_status: d.paid_via ? "paid" : "unpaid",
          bill_number: d.bill_number || d.invoice_number || "",
          bill_date: d.bill_date || d.date || "",
          line_items: scannedItems,
          interstate,
          is_eligible_itc: true,
          reverse_charge: false,
          receipt_image: dataUrl,
        });
        setDialogOpen(true);
        const pageNote = isPdf && d.page_count > 1 ? ` (${d.page_count} pages, ${scannedItems.length} items)` : "";
        if (d.vendor_is_new) {
          toast.success(`Invoice scanned${pageNote} — '${d.vendor_name}' added to your Vendors!`);
        } else {
          toast.success(`Invoice scanned${pageNote} — review and save.`);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to scan ${isPdf ? "PDF" : "invoice"}`);
      } finally {
        setScanning(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleSave = async () => {
    if (!form.vendor_name) { toast.error("Supplier name required"); return; }
    const validItems = (form.line_items || []).filter((li) => (li.name || "").trim() || Number(li.unit_price) > 0);
    if (validItems.length === 0) {
      toast.error("Add at least one line item with a name or price");
      return;
    }
    if (totals.grand_total <= 0) {
      toast.error("Grand total must be greater than zero");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        line_items: validItems,
        amount: totals.grand_total,
        cgst: totals.cgst,
        sgst: totals.sgst,
        igst: totals.igst,
        gst_rate: validItems[0]?.gst_rate_pct || 0,
      };
      if (editId) {
        const res = await api.put(`/purchases/${editId}`, payload);
        setPurchases(purchases.map((p) => p.purchase_id === editId ? res.data : p));
        toast.success("Purchase updated");
      } else {
        const res = await api.post("/purchases", payload);
        setPurchases([res.data, ...purchases]);
        const sync = res.data?.inventory_sync;
        if (sync && sync.created > 0) {
          toast.success(`Purchase recorded — ${sync.created} new item${sync.created > 1 ? "s" : ""} added to inventory`);
        } else {
          toast.success("Purchase recorded");
        }
        if (cameFromCcScan) {
          try {
            localStorage.setItem("bizflowa.last_cc_save", JSON.stringify({
              at: Date.now(),
              kind: "purchase",
              id: res.data?.purchase_id || "",
              vendor_name: res.data?.vendor_name || payload.vendor_name || "",
              amount: res.data?.amount || payload.amount || 0,
            }));
          } catch { /* localStorage may be disabled — ignore */ }
        }
      }
      setDialogOpen(false);
      resetForm();
      setCameFromCcScan(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/purchases/${id}`);
      setPurchases(purchases.filter((p) => p.purchase_id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handleMarkPaid = async (id) => {
    try {
      const res = await api.put(`/purchases/${id}/mark-paid`, { payment_date: new Date().toISOString().split("T")[0] });
      setPurchases(purchases.map((p) => p.purchase_id === id ? res.data : p));
      toast.success("Marked as paid");
    } catch { toast.error("Failed to mark paid"); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((p) => p.purchase_id)));
  };

  const bulkDelete = async () => {
    try {
      await api.post("/purchases/bulk-delete", { ids: Array.from(selected) });
      setPurchases(purchases.filter((p) => !selected.has(p.purchase_id)));
      setSelected(new Set());
      toast.success("Deleted selected purchases");
    } catch { toast.error("Bulk delete failed"); }
  };
  const bulkMarkPaid = async () => {
    try {
      await api.post("/purchases/bulk-mark-paid", { ids: Array.from(selected), paid_via: "Bank Transfer" });
      const refreshed = await api.get("/purchases");
      setPurchases(refreshed.data);
      setSelected(new Set());
      toast.success("Marked selected as paid");
    } catch { toast.error("Bulk mark paid failed"); }
  };

  const filtered = useMemo(() => {
    let items = purchases;
    if (filter === "paid") items = items.filter((p) => p.payment_status === "paid");
    if (filter === "unpaid") items = items.filter((p) => p.payment_status !== "paid");
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((p) =>
        (p.vendor_name || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
      );
    }
    return items;
  }, [purchases, filter, search]);

  const totalAmount = filtered.reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) return <div className="p-8 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4338ca]" /></div>;

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="purchases-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Purchases</h1>
          <p className="text-sm text-[#64748b] mt-1">Direct costs — COGS, raw materials, inventory</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => navigate("/purchases/import")} data-testid="import-purchases-btn" className="border-[#e2e8f0] rounded-xl">
              <Upload className="h-4 w-4 mr-2" /> Import
            </Button>
            <Button variant="outline" onClick={() => scanRef.current?.click()} disabled={scanning}
              data-testid="scan-purchase-invoice-btn" className="border-[#e2e8f0] rounded-xl">
              {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ScanLine className="h-4 w-4 mr-2" />}
              {scanning ? "Scanning..." : "Scan Invoice"}
            </Button>
            <input ref={scanRef} type="file" accept="image/*,application/pdf" onChange={handleScanInvoice}
              className="hidden" data-testid="purchase-scan-input" />
            <Button onClick={openCreate} data-testid="create-purchase-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">
              <Plus className="h-4 w-4 mr-2" /> Record Purchase
            </Button>
          </div>

          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-outfit text-xl">{editId ? "Edit Purchase" : "Record Purchase"}</DialogTitle>
              <DialogDescription className="text-sm text-[#64748b]">Direct cost entry — goes to COGS in your P&L. New items auto-sync to your inventory master.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Supplier + Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Supplier Name *</Label>
                  <Input value={form.vendor_name} onChange={(e) => handleChange("vendor_name", e.target.value)} placeholder="e.g., Steel Corp" data-testid="purchase-vendor-input" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <Select value={form.category || "none"} onValueChange={(v) => handleChange("category", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="purchase-category-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select category</SelectItem>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date + Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => handleChange("date", e.target.value)} data-testid="purchase-date-input" className="mt-1.5" />
                </div>
                {!isUnregistered && (
                  <div>
                    <Label className="text-sm font-medium">Due Date</Label>
                    <Input type="date" value={form.due_date} onChange={(e) => handleChange("due_date", e.target.value)} className="mt-1.5" />
                  </div>
                )}
              </div>

              {/* Supplier Invoice No. + Date — populates Tally's REFERENCE/REFERENCEDATE */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Supplier Invoice No.</Label>
                  <Input value={form.bill_number} onChange={(e) => handleChange("bill_number", e.target.value)}
                    placeholder="e.g., INV/2025-26/123"
                    data-testid="purchase-bill-number-input" className="mt-1.5 font-mono" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Supplier Invoice Date</Label>
                  <Input type="date" value={form.bill_date} onChange={(e) => handleChange("bill_date", e.target.value)}
                    data-testid="purchase-bill-date-input" className="mt-1.5" />
                </div>
              </div>

              {/* GST Number + Interstate toggle */}
              {!isUnregistered && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">GST Number</Label>
                    <Input value={form.gst_number} onChange={(e) => handleChange("gst_number", e.target.value)} placeholder="Supplier GSTIN" data-testid="purchase-gst-input" className="mt-1.5 font-mono" />
                  </div>
                  <div className="flex flex-col gap-1 mt-7">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={form.interstate}
                        onCheckedChange={(v) => handleChange("interstate", !!v)}
                        data-testid="purchase-interstate-checkbox" />
                      <Label className="text-sm font-medium cursor-pointer" onClick={() => handleChange("interstate", !form.interstate)}>
                        Interstate supply (IGST)
                      </Label>
                    </div>
                    {autoInterstateNote && (
                      <p className="text-[11px] text-[#4338ca] pl-6" data-testid="purchase-interstate-auto-note">
                        ⚡ {autoInterstateNote}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Line items */}
              <div className="bg-[#f8fafc] rounded-xl border border-[#e2e8f0] p-4">
                <PurchaseLineItemsEditor
                  items={form.line_items}
                  onChange={(items) => handleChange("line_items", items)}
                  interstate={form.interstate}
                />
              </div>

              {/* Tax breakout */}
              {!isUnregistered && (
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="purchase-tax-breakout">
                  <div>
                    <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium">Taxable Value</p>
                    <p className="text-sm font-mono font-semibold text-[#0f172a] mt-0.5" data-testid="purchase-taxable-value">
                      {formatINR(totals.taxable_value)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium">CGST</p>
                    <p className={`text-sm font-mono font-semibold mt-0.5 ${form.interstate ? "text-[#94a3b8]" : "text-[#0f172a]"}`}
                      data-testid="purchase-total-cgst">
                      {formatINR(totals.cgst)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium">SGST</p>
                    <p className={`text-sm font-mono font-semibold mt-0.5 ${form.interstate ? "text-[#94a3b8]" : "text-[#0f172a]"}`}
                      data-testid="purchase-total-sgst">
                      {formatINR(totals.sgst)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium">IGST</p>
                    <p className={`text-sm font-mono font-semibold mt-0.5 ${form.interstate ? "text-[#0f172a]" : "text-[#94a3b8]"}`}
                      data-testid="purchase-total-igst">
                      {formatINR(totals.igst)}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between bg-[#4338ca]/5 border border-[#c7d2fe] rounded-xl px-4 py-3">
                <span className="text-sm font-medium text-[#4338ca]">Grand Total (Amount Payable)</span>
                <span className="text-xl font-mono font-bold text-[#0f172a]" data-testid="purchase-grand-total">
                  {formatINR(totals.grand_total)}
                </span>
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm font-medium">Description / Notes</Label>
                <Input value={form.description} onChange={(e) => handleChange("description", e.target.value)} placeholder="Purchase details" data-testid="purchase-description-input" className="mt-1.5" />
              </div>

              {/* Payment */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Payment Status</Label>
                  <Select value={form.payment_status} onValueChange={(v) => handleChange("payment_status", v)}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unpaid">Unpaid</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.payment_status === "paid" && (
                  <div>
                    <Label className="text-sm font-medium">Paid Via</Label>
                    <Select value={form.paid_via || "none"} onValueChange={(v) => handleChange("paid_via", v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select</SelectItem>
                        {PAID_VIA_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Compliance (hidden for unregistered) */}
              {!isUnregistered && (
                <div className="bg-[#f8fafc] rounded-xl border border-[#e2e8f0] p-4 space-y-3">
                  <Label className="text-sm font-semibold text-[#0f172a]">Compliance</Label>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={form.is_eligible_itc} onCheckedChange={(v) => handleChange("is_eligible_itc", v)} data-testid="purchase-itc-checkbox" />
                      <span className="text-sm text-[#0f172a]">ITC Eligible</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={form.reverse_charge} onCheckedChange={(v) => handleChange("reverse_charge", v)} data-testid="purchase-rcm-checkbox" />
                      <span className="text-sm text-[#0f172a]">Reverse Charge (RCM)</span>
                    </label>
                  </div>
                </div>
              )}

              {/* File Upload */}
              <div>
                <Label className="text-sm font-medium">Attach Bill / Voucher</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-lg">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload
                  </Button>
                  {form.receipt_image && <Badge className="bg-[#10b981]/10 text-[#10b981]"><CheckCircle2 className="h-3 w-3 mr-1" /> Attached</Badge>}
                  <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={handleFileUpload} className="hidden" data-testid="purchase-attach-input" />
                </div>
              </div>

              <Button onClick={handleSave} disabled={saving} data-testid="save-purchase-btn" className="w-full bg-[#4338ca] hover:bg-[#3730a3] rounded-xl">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editId ? "Update Purchase" : "Record Purchase"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-white border-[#e2e8f0]">
          <CardContent className="p-3">
            <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium">Total Purchases</p>
            <p className="font-outfit text-lg font-bold text-[#0f172a] mt-0.5">{purchases.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#e2e8f0]">
          <CardContent className="p-3">
            <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium">Total COGS</p>
            <p className="font-outfit text-lg font-bold text-[#0f172a] mt-0.5 font-mono">{formatINR(totalAmount)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#e2e8f0]">
          <CardContent className="p-3">
            <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium">Unpaid</p>
            <p className="font-outfit text-lg font-bold text-[#f59e0b] mt-0.5">{purchases.filter((p) => p.payment_status !== "paid").length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#e2e8f0]">
          <CardContent className="p-3">
            <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium">Paid</p>
            <p className="font-outfit text-lg font-bold text-[#10b981] mt-0.5">{purchases.filter((p) => p.payment_status === "paid").length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search supplier, category..." data-testid="purchases-search" className="pl-9 h-10 border-[#e2e8f0]" />
        </div>
        <div className="flex gap-1.5">
          {["all", "unpaid", "paid"].map((f) => (
            <Button key={f} variant={filter === f ? "default" : "outline"} size="sm"
              onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`rounded-lg text-xs capitalize ${filter === f ? "bg-[#4338ca] text-white" : "border-[#e2e8f0]"}`}>
              {f}
            </Button>
          ))}
        </div>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 p-2.5 bg-[#4338ca]/5 rounded-lg border border-[#4338ca]/10">
          <Badge className="bg-[#4338ca] text-white text-xs">{selected.size} selected</Badge>
          <Button size="sm" variant="outline" onClick={bulkMarkPaid} className="text-xs rounded-lg border-[#10b981] text-[#10b981]">
            <CheckCircle2 className="h-3 w-3 mr-1" /> Mark Paid
          </Button>
          <Button size="sm" variant="ghost" onClick={bulkDelete} className="text-xs text-red-500 hover:text-red-600 rounded-lg">
            <Trash2 className="h-3 w-3 mr-1" /> Delete
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="text-xs text-[#64748b]">Clear</Button>
        </div>
      )}

      {/* Table */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <ShoppingCart className="h-12 w-12 text-[#e2e8f0] mx-auto mb-3" />
              <p className="text-sm text-[#64748b]">{search ? "No purchases match." : "No purchases recorded yet."}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                  <th className="py-3 px-4 w-10">
                    <Checkbox checked={selected.size === filtered.length && filtered.length > 0} onCheckedChange={toggleAll} />
                  </th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Supplier</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Category</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Date</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Amount</th>
                  <th className="text-center py-3 px-4 text-[#64748b] font-medium">Status</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((pur) => (
                  <tr key={pur.purchase_id} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`purchase-row-${pur.purchase_id}`}>
                    <td className="py-3 px-4">
                      <Checkbox checked={selected.has(pur.purchase_id)} onCheckedChange={() => toggleSelect(pur.purchase_id)} />
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-medium text-[#0f172a]">{pur.vendor_name}</div>
                      {pur.description && <div className="text-xs text-[#94a3b8] truncate max-w-[200px]">{pur.description}</div>}
                      {!isUnregistered && (
                        <div className="flex items-center gap-1 mt-0.5">
                          {pur.is_eligible_itc === false && <Badge className="bg-red-50 text-red-400 text-[9px] px-1 py-0">ITC Blocked</Badge>}
                          {pur.reverse_charge && <Badge className="bg-[#4338ca]/10 text-[#4338ca] text-[9px] px-1 py-0">RCM</Badge>}
                        </div>
                      )}
                      {pur.receipt_drive_link && (
                        <a href={pur.receipt_drive_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-0.5 text-[10px] text-[#4338ca] hover:underline" data-testid={`receipt-link-${pur.purchase_id}`}>
                          <HardDrive className="h-3 w-3" /> View Receipt
                        </a>
                      )}
                    </td>
                    <td className="py-3 px-4 text-[#64748b]">{pur.category || "-"}</td>
                    <td className="py-3 px-4 text-[#64748b]">{pur.date}</td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-[#0f172a]">{formatINR(pur.amount)}</td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={pur.payment_status === "paid" ? "bg-[#10b981]/10 text-[#10b981]" : "bg-[#f59e0b]/10 text-[#f59e0b]"}>
                        {pur.payment_status === "paid" ? "Paid" : "Unpaid"}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreVertical className="h-3.5 w-3.5" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(pur)}><Pencil className="h-3 w-3 mr-2" /> Edit</DropdownMenuItem>
                          {pur.payment_status !== "paid" && (
                            <DropdownMenuItem onClick={() => handleMarkPaid(pur.purchase_id)}><CheckCircle2 className="h-3 w-3 mr-2" /> Mark Paid</DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDelete(pur.purchase_id)} className="text-red-500">
                            <Trash2 className="h-3 w-3 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
